import React, { useState } from 'react';
import { Plus, GripVertical, Edit2, Trash2 } from 'lucide-react';
import { TableConfig } from '@/app/types/table';
import { PlacedBlock, SocketConnection } from '@/app/types/cad';
import { collectAllAttributes } from '@/app/utils/tableUtils';
import { AddAttributeDialog, AddAttributeConfig } from './AddAttributeDialog';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface TableSidebarProps {
  tableConfig: TableConfig;
  blocks: PlacedBlock[];
  connections: SocketConnection[];
  onAddAttributeColumn: (attributeName: string) => void;
  onCreateAttribute?: (config: AddAttributeConfig) => void;
  onEditAttribute?: (oldName: string, newConfig: AddAttributeConfig) => void;
  onDeleteAttribute?: (attributeName: string) => void;
}

export function TableSidebar({
  tableConfig,
  blocks,
  connections,
  onAddAttributeColumn,
  onCreateAttribute,
  onEditAttribute,
  onDeleteAttribute,
}: TableSidebarProps) {
  const { t } = useLanguage();
  const [draggedAttribute, setDraggedAttribute] = useState<string | null>(null);
  const [showAddAttributeDialog, setShowAddAttributeDialog] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState<string | null>(null);

  // Collect all attributes
  const allAttributes = collectAllAttributes(blocks, connections);

  // Check which attributes are already in columns
  const usedAttributes = new Set(
    tableConfig.columns.map(col => col.attributeKey).filter(Boolean)
  );

  const handleDragStart = (attributeName: string) => (e: React.DragEvent) => {
    setDraggedAttribute(attributeName);
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', attributeName);
  };

  const handleDragEnd = () => {
    setDraggedAttribute(null);
  };

  return (
    <div
      className="w-full h-full flex flex-col"
      style={{
        backgroundColor: 'var(--md-surface-container-low)',
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-3 border-b flex items-center justify-between"
        style={{ borderColor: 'var(--md-outline)' }}
      >
        <h3
          className="font-semibold text-sm"
          style={{ color: 'var(--md-on-surface)' }}
        >
          {t('table.sidebar.attributes')}
        </h3>
        <button
          className="p-1 rounded hover:bg-opacity-10"
          style={{
            color: 'var(--md-primary)',
          }}
          title={t('table.sidebar.createAttribute')}
          onClick={() => setShowAddAttributeDialog(true)}
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Attribute List */}
      <div className="flex-1 overflow-y-auto px-2 py-2">
        {allAttributes.size === 0 ? (
          <div
            className="px-4 py-8 text-center text-sm"
            style={{ color: 'var(--md-on-surface-variant)' }}
          >
            {t('table.sidebar.noAttributes')}
            <div className="text-xs mt-1">
              {t('table.sidebar.clickToAdd')}
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            {Array.from(allAttributes.entries())
              .sort((a, b) => a[0].localeCompare(b[0]))
              .map(([name, info]) => {
                const isUsed = usedAttributes.has(name);
                const isDragging = draggedAttribute === name;

                return (
                  <div
                    key={name}
                    className="px-3 py-2 rounded transition-all relative group flex items-center justify-between gap-2"
                    style={{
                      backgroundColor: isDragging
                        ? 'rgba(var(--md-primary-rgb, 103, 80, 164), 0.15)'
                        : isUsed
                        ? 'rgba(var(--md-primary-rgb, 103, 80, 164), 0.08)'
                        : 'transparent',
                      opacity: isDragging ? 0.5 : 1,
                      border: isUsed ? '1px solid var(--md-primary)' : '1px solid transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (!isDragging && !isUsed) {
                        e.currentTarget.style.backgroundColor = 'rgba(var(--md-on-surface-rgb, 0, 0, 0), 0.05)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isDragging && !isUsed) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {/* DRAGGABLE AREA - Left side only! */}
                    <div
                      className="flex items-center space-x-2 flex-1 min-w-0 cursor-grab active:cursor-grabbing"
                      draggable
                      onDragStart={handleDragStart(name)}
                      onDragEnd={handleDragEnd}
                    >
                      <GripVertical
                        className="w-4 h-4 flex-shrink-0"
                        style={{ color: 'var(--md-on-surface-variant)' }}
                      />
                      <div className="flex-1 min-w-0">
                        <div
                          className="font-medium text-sm truncate"
                          style={{ color: 'var(--md-on-surface)' }}
                        >
                          {name}
                        </div>
                        <div
                          className="text-xs mt-0.5 flex items-center space-x-2"
                          style={{ color: 'var(--md-on-surface-variant)' }}
                        >
                          <span>{info.count}× {t('table.sidebar.used')}</span>
                          <span>•</span>
                          <span
                            className="px-1.5 py-0.5 rounded text-xs"
                            style={{
                              backgroundColor: 'var(--md-secondary-container)',
                              color: 'var(--md-on-secondary-container)',
                            }}
                          >
                            {info.objectType === 'both'
                              ? t('table.sidebar.types.both')
                              : info.objectType === 'block'
                              ? t('table.sidebar.types.block')
                              : t('table.sidebar.types.connection')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* BUTTONS - Right side, NOT draggable! */}
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      {onEditAttribute && (
                        <button
                          className="p-1 rounded hover:bg-white hover:bg-opacity-20"
                          style={{
                            color: 'var(--md-primary)',
                          }}
                          title={t('table.sidebar.editAttribute')}
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('✏️ Edit clicked for:', name);
                            setEditingAttribute(name);
                          }}
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {onDeleteAttribute && (
                        <button
                          className="p-1 rounded hover:bg-white hover:bg-opacity-20"
                          style={{
                            color: '#d32f2f',
                          }}
                          title={t('table.sidebar.deleteAttribute')}
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('🗑️ Delete clicked for:', name);
                            if (confirm(t('table.sidebar.confirmDelete', { name }))) {
                              console.log('✅ User confirmed delete');
                              onDeleteAttribute(name);
                            } else {
                              console.log('❌ User cancelled delete');
                            }
                          }}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div
        className="px-4 py-3 border-t text-xs"
        style={{
          borderColor: 'var(--md-outline)',
          color: 'var(--md-on-surface-variant)',
        }}
      >
        {allAttributes.size} {t('table.sidebar.attributes')} {t('table.sidebar.total')}
        <div className="mt-1">
          {t('table.sidebar.dragHint')}
        </div>
      </div>

      {/* Add Attribute Dialog */}
      {showAddAttributeDialog && onCreateAttribute && (
        <AddAttributeDialog
          open={showAddAttributeDialog}
          onClose={() => setShowAddAttributeDialog(false)}
          onConfirm={(config) => {
            onCreateAttribute(config);
            setShowAddAttributeDialog(false);
          }}
          blocks={blocks}
          connections={connections}
        />
      )}

      {/* Edit Attribute Dialog */}
      {editingAttribute && onEditAttribute && (
        <AddAttributeDialog
          open={true}
          onClose={() => setEditingAttribute(null)}
          onConfirm={(config) => {
            onEditAttribute(editingAttribute, config);
            setEditingAttribute(null);
          }}
          blocks={blocks}
          connections={connections}
          initialName={editingAttribute}
          isEditMode={true}
        />
      )}
    </div>
  );
}
